<script setup lang="ts">
/**
 * Where a signer draws their signature — or has their typed name drawn for
 * them in a handwriting face.
 *
 * Either way the result is the same thing: a transparent PNG from this canvas.
 * The server takes nothing else (see the API's utils/signaturePng.ts), so the
 * typed mode is not a text field in disguise: it is pixels, and goes through
 * exactly the same checks as a drawn signature.
 *
 * The backing store is a fixed 1200×400 whatever the screen — 3:1 is the shape
 * of a signature line, and a fixed size keeps every upload well under the
 * server's limits on a high-density phone. Pointer events cover mouse, pen and
 * touch alike; `touch-action: none` stops the page scrolling under a finger
 * that is trying to sign.
 */
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Eraser, Undo2 } from 'lucide-vue-next'

const props = defineProps<{
  mode: 'draw' | 'type'
  /** What the typed mode writes. */
  typedText: string
}>()

const emit = defineEmits<{
  /** Whether there is anything on the canvas to sign with. */
  change: [hasInk: boolean]
}>()

const { t } = useI18n()

const WIDTH = 1200
const HEIGHT = 400
const INK = '#111827'

/**
 * System handwriting faces, most platforms have one of them. No web font: the
 * signing page loads nothing from third parties, and a signature that renders
 * in the platform's cursive fallback is still a typed signature.
 */
const SCRIPT_FONTS = '"Segoe Script", "Snell Roundhand", "Brush Script MT", "Lucida Handwriting", "Apple Chancery", cursive'

const canvas = ref<HTMLCanvasElement | null>(null)
type Point = { x: number; y: number }
const strokes = ref<Point[][]>([])
let drawing: Point[] | null = null

function context(): CanvasRenderingContext2D | null {
  return canvas.value?.getContext('2d') ?? null
}

function redraw(): void {
  const ctx = context()
  if (!ctx) return
  ctx.clearRect(0, 0, WIDTH, HEIGHT)

  if (props.mode === 'type') {
    const text = props.typedText.trim()
    if (text) {
      // Largest size that fits, so a long name still fits on the line.
      let size = 150
      ctx.font = `${size}px ${SCRIPT_FONTS}`
      while (size > 30 && ctx.measureText(text).width > WIDTH * 0.9) {
        size -= 6
        ctx.font = `${size}px ${SCRIPT_FONTS}`
      }
      ctx.fillStyle = INK
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, WIDTH / 2, HEIGHT * 0.55)
    }
    emit('change', text.length > 0)
    return
  }

  ctx.strokeStyle = INK
  ctx.lineWidth = 7
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (const stroke of strokes.value) {
    ctx.beginPath()
    stroke.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)))
    // A tap is a dot: a one-point path draws nothing without this.
    if (stroke.length === 1) ctx.lineTo(stroke[0].x + 0.1, stroke[0].y)
    ctx.stroke()
  }
  emit('change', strokes.value.length > 0)
}

function toCanvas(event: PointerEvent): Point {
  const rect = canvas.value!.getBoundingClientRect()
  return { x: ((event.clientX - rect.left) / rect.width) * WIDTH, y: ((event.clientY - rect.top) / rect.height) * HEIGHT }
}

function start(event: PointerEvent): void {
  if (props.mode !== 'draw') return
  canvas.value?.setPointerCapture(event.pointerId)
  drawing = [toCanvas(event)]
  strokes.value.push(drawing)
  redraw()
}

function move(event: PointerEvent): void {
  if (!drawing) return
  drawing.push(toCanvas(event))
  redraw()
}

function end(): void {
  drawing = null
}

function undo(): void {
  strokes.value.pop()
  redraw()
}

function clear(): void {
  strokes.value = []
  redraw()
}

/** The signature as a PNG data URL, or null when there is none. */
function toDataUrl(): string | null {
  const hasInk = props.mode === 'type' ? props.typedText.trim().length > 0 : strokes.value.length > 0
  return hasInk && canvas.value ? canvas.value.toDataURL('image/png') : null
}

defineExpose({ toDataUrl })

watch(() => [props.mode, props.typedText], redraw)
onMounted(redraw)
</script>

<template>
  <div>
    <div class="relative rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white">
      <canvas
        ref="canvas"
        :width="WIDTH"
        :height="HEIGHT"
        class="block w-full aspect-[3/1] rounded-lg"
        :class="mode === 'draw' ? 'cursor-crosshair' : ''"
        style="touch-action: none"
        :aria-label="mode === 'draw' ? t('signDrawHint') : t('signTypedPreview')"
        role="img"
        @pointerdown.prevent="start"
        @pointermove.prevent="move"
        @pointerup="end"
        @pointercancel="end"
        @pointerleave="end"
      />
      <div class="pointer-events-none absolute left-6 right-6 bottom-[22%] border-b border-gray-300" />
      <p
        v-if="mode === 'draw' && strokes.length === 0"
        class="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400"
      >
        {{ t('signDrawHint') }}
      </p>
    </div>
    <div v-if="mode === 'draw'" class="mt-2 flex justify-end gap-2">
      <button
        type="button"
        :disabled="strokes.length === 0"
        class="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-40"
        @click="undo"
      >
        <Undo2 :size="14" /> {{ t('signUndo') }}
      </button>
      <button
        type="button"
        :disabled="strokes.length === 0"
        class="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-40"
        @click="clear"
      >
        <Eraser :size="14" /> {{ t('signClear') }}
      </button>
    </div>
  </div>
</template>
